import { v4 as uuidv4 } from 'uuid';
import {
  CircuitSchema,
  ComponentOverride,
  OperatingScenario,
  SimulationParams,
} from '../types';
import { migratePumpProperties } from './componentDefaults';

/**
 * Brings a circuit schema from any source — localStorage, a template, a cloud
 * save, a shared link, an imported XML/JSON file — into the shape the app
 * expects.
 *
 * Schemas reach the app in several historical shapes: scenarios stored at the
 * top level instead of inside `simulation_params`, overrides keyed
 * `overrides`/`heat_input_kw` instead of `component_overrides`/`heat_input`,
 * and scenarios without ids. Reading those unguarded used to throw during
 * render (`simulation_params.scenarios.length` on a `simulation_params` that
 * only carried `fluid_id`), which unmounted the whole app.
 */

export function emptySimulationParams(): SimulationParams {
  return { fluid_id: null, scenarios: [] };
}

function normalizeOverride(raw: any): ComponentOverride {
  if (!raw || typeof raw !== 'object') return {};
  const override: ComponentOverride = {};
  // `heat_input_kw` is the legacy key; the solver reads `heat_input`.
  const heat = raw.heat_input ?? raw.heat_input_kw;
  if (typeof heat === 'number') override.heat_input = heat;
  if (typeof raw.pump_flow === 'number') override.pump_flow = raw.pump_flow;
  return override;
}

function normalizeOverrides(raw: any): Record<string, ComponentOverride> {
  if (!raw || typeof raw !== 'object') return {};
  const result: Record<string, ComponentOverride> = {};
  for (const [nodeId, override] of Object.entries(raw)) {
    const normalized = normalizeOverride(override);
    if (Object.keys(normalized).length > 0) result[nodeId] = normalized;
  }
  return result;
}

function normalizeScenario(raw: any, index: number): OperatingScenario {
  return {
    id: typeof raw?.id === 'string' && raw.id ? raw.id : uuidv4(),
    name: typeof raw?.name === 'string' && raw.name ? raw.name : `Scenario ${index + 1}`,
    ambient_temperature:
      typeof raw?.ambient_temperature === 'number' ? raw.ambient_temperature : 35,
    // `overrides` is the legacy key.
    component_overrides: normalizeOverrides(raw?.component_overrides ?? raw?.overrides),
  };
}

export function normalizeScenarios(raw: any): OperatingScenario[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeScenario);
}

/**
 * @param legacyScenarios scenarios found at the top level of a schema, used
 *   only when `simulation_params` carries none of its own.
 */
export function normalizeSimulationParams(
  raw: any,
  legacyScenarios?: any,
): SimulationParams {
  const scenarios = Array.isArray(raw?.scenarios) && raw.scenarios.length > 0
    ? raw.scenarios
    : legacyScenarios;

  return {
    fluid_id: typeof raw?.fluid_id === 'number' ? raw.fluid_id : null,
    scenarios: normalizeScenarios(scenarios),
  };
}

/**
 * The coolant used to be chosen once per canvas (`simulation_params.fluid_id`);
 * it is now chosen on each pump. Pumps of an older schema that have none get
 * the old canvas-wide coolant, so the circuit simulates exactly as before.
 */
function migrateCanvasCoolant(nodes: any[], legacyFluidId: number | null): any[] {
  if (!legacyFluidId) return nodes;
  return nodes.map(n => {
    if (n?.data?.component?.component_type !== 'pump' || n.data.properties?.fluid_id) return n;
    return { ...n, data: { ...n.data, properties: { ...(n.data.properties || {}), fluid_id: legacyFluidId } } };
  });
}

export function normalizeSchema(raw: any): CircuitSchema {
  const simulation_params = normalizeSimulationParams(raw?.simulation_params, raw?.scenarios);
  const nodes = Array.isArray(raw?.nodes) ? raw.nodes : [];
  return {
    nodes: migratePumpProperties(migrateCanvasCoolant(nodes, simulation_params.fluid_id)),
    edges: Array.isArray(raw?.edges) ? raw.edges : [],
    annotations: Array.isArray(raw?.annotations) ? raw.annotations : [],
    viewport: raw?.viewport,
    simulation_params,
  };
}
